import { Button } from '@/components/ui/therapist/Button';
import encryptedStorage from '@/stores/encryptedStorage';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { isExerciseSet } from '@/utils/isExerciseSet';
import { IS_IOS } from '@/utils/platform';
import { logError } from '@/utils/logHandling';
import { OnActionType } from '@/views/SetCard/SetCardMenu';
import { Patient, SetType } from '@/stores/store.types';
import { StackNavigationProp } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { View } from 'react-native';
import { useAssetDownloader } from '@/contexts/DownloadAssetContext';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { useEffect, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useStoreActions, useStoreState } from '@/stores/store';
import { styles } from './styles';
import { useSetsMode } from '@/contexts/SetsModeContext/useSetsMode';

type ExerciseButtonProps = {
  canStartPracticing?: boolean;
  id: string;
  onActionPress: (value: OnActionType) => void;
  type: SetType;
};

export const ExerciseButton = ({
  canStartPracticing,
  id,
  onActionPress,
  type,
}: ExerciseButtonProps) => {
  const [isSync, setIsSync] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const { isTherapistDemo, refresh, toolTipNumber } = useStoreState(
    (state) => state,
  );
  const { mode } = useSetsMode();

  const { setGlobalPopupState, setToolTipNumber } = useStoreActions(
    (actions) => ({
      setGlobalPopupState: actions.setGlobalPopupState,
      setToolTipNumber: actions.setToolTipNumber,
    }),
  );
  const {
    downloadedExercises,
    patientsSyncing: patientsSyncingRaw,
    selectedID,
    showStorageWarningDialog,
  } = useDashboardContext();
  const patientsSyncing = patientsSyncingRaw as (
    patientsParameter?: Patient[] | null,
    stackChanges?: boolean,
  ) => void;
  const { hasFreeStorage } = useAssetDownloader();
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();

  const setLocalSetSync = async (setId: string, value: boolean) => {
    const localSet = await encryptedStorage().getItem(setId);
    if (localSet) {
      await encryptedStorage().setItem(setId, { ...localSet, sync: value });
    }
  };

  const revertLocalSetStructure = async (setId: string) => {
    let localSet = await encryptedStorage().getItem(setId);

    if (isExerciseSet(localSet)) {
      let {
        activeWordset,
        assets,
        videosAdded,
        ablenkerFromSetJson,
        ...whatWeNeed
      } = localSet;
      whatWeNeed.preparedSet = {};
      whatWeNeed.preparedSet.date = '';
      await encryptedStorage().setItem(setId, whatWeNeed);
    } else {
      logError(
        `Cannot revert set structure, not an exercise set with id ${setId}`,
      );
    }
  };

  const handleLoadChange = async (newValue: boolean) => {
    if (isTherapistDemo) {
      if (newValue === true && toolTipNumber === 1) {
        setToolTipNumber(2);
      }
      if (newValue === false && toolTipNumber === 2) {
        setToolTipNumber(1);
      }
    }

    if (newValue) {
      const hasInternet = await hasInternetAsync();
      const hasFreeStorageResult = await hasFreeStorage();
      if (!hasInternet) {
        setIsSync(false);
        setGlobalPopupState({
          headerText: 'Keine Verbindung zum neolexon Server',
          bodyText:
            'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
          leftButton: '',
          rightButton: '',
        });
      } else if (!hasFreeStorageResult) {
        setIsSync(false);
        showStorageWarningDialog();
      } else {
        await setLocalSetSync(id, newValue);
        await patientsSyncing();
        setIsSync(true);
      }
    } else {
      await setLocalSetSync(id, newValue);
      await revertLocalSetStructure(id);
      await patientsSyncing();
      setIsSync(false);
    }
  };

  const startExercise = async () => {
    let className = '';
    if (type == 'Wörtersets') {
      className = 'wordset';
    }
    if (type == 'Sätzesets') {
      className = 'sentenceset';
    }
    if (type == 'Textesets') {
      className = 'textset';
    }
    if (isTherapistDemo && toolTipNumber === 2) {
      setToolTipNumber(4);
    }
    const exerciseSettings = (await encryptedStorage().getItem(id)) as any;
    // @ts-ignore
    navigate('Aufgaben', {
      exerciseClass: className,
      patientId: selectedID,
      isSelf: false,
      setId: id,
      exerciseSettings,
    });
  };

  useEffect(() => {
    let isMounted = true;

    const checkIfDownloaded = async () => {
      const localSet: any = await encryptedStorage().getItem(id);
      if (!isMounted) {
        return;
      }
      setIsSync(!!localSet?.sync);
      setIsLoaded(!!localSet?.activeWordset);
    };

    checkIfDownloaded();

    return () => {
      isMounted = false;
    };
  }, [downloadedExercises, id, refresh]);

  useEffect(() => {
    // sometimes the tooltipnumber is false, which is strange since in the store itself it's set to 1. So just in case we have this code
    if (isTherapistDemo && (toolTipNumber as unknown) === false) {
      setToolTipNumber(1);
    }
  }, [isSync]);

  const isTextesets = type === 'Textesets';
  const startBildkarten = async () => {
    let className;
    if (type == 'Wörtersets') {
      className = 'wordset' as const;
    }
    if (type == 'Sätzesets') {
      className = 'sentenceset' as const;
    }

    const data: any = await encryptedStorage().getItem(id);

    if (isExerciseSet(data)) {
      navigate('BildKarten', {
        exerciseClass: className,
        setId: id,
        cardSettings: data?.cardSettings,
      });
    } else {
      logError(
        `Cannot navigate to BildKarten, not an exercise set with id ${id}`,
      );
    }
  };

  if (mode === 'bildkarten') {
    return (
      <>
        {isLoaded ? (
          <Button
            label="Bildkarten anzeigen"
            look="secondary"
            icon="playOutline"
            iconStyle={IS_IOS ? { marginRight: -8 } : {}}
            onPress={startBildkarten}
            style={styles.btnWithIcon}
          />
        ) : (
          <Button
            label="Laden"
            look="secondary"
            icon="download"
            iconSize={18}
            onPress={() => handleLoadChange(!isLoaded)}
            style={styles.btnWithoutIcon}
          />
        )}
      </>
    );
  }

  return (
    <>
      {!canStartPracticing ? (
        isTextesets ? (
          <View style={{ flex: 1, height: 52 }} />
        ) : (
          <Button
            label="Ergebnisse"
            look="secondary"
            onPress={() => onActionPress('results')}
            style={styles.btnWithoutIcon}
          />
        )
      ) : (
        <>
          {isLoaded ? (
            <Button
              label="Jetzt üben"
              look="secondary"
              icon="playOutline"
              iconStyle={IS_IOS ? { marginRight: -8 } : {}}
              onPress={startExercise}
              style={styles.btnWithIcon}
            />
          ) : (
            <Button
              label="Laden"
              look="secondary"
              icon="download"
              iconSize={18}
              onPress={() => handleLoadChange(!isLoaded)}
              style={styles.btnWithoutIcon}
            />
          )}
        </>
      )}
    </>
  );
};
